import React from "react";
import { Button, Paper, Typography } from "@mui/material";
import { BtmReturnRequest } from "../model/btm-return-request";
import { ArrowBack, Article, Category, Download, LocalShipping, Person } from "@mui/icons-material";
import { BtmRequestLogisticSetup } from "./btm-request-logistic-setup";
import { BtmRequestStatusChip } from "../btm-request-status-chip/btm-request-status-chip";
import FileSaver from "file-saver";
import { FileExchangeToFile } from "../../../../helpers/file-exchange-to-file";
import { VPUITheme } from "../../../../common/theme/vpui-theme";
import { BtmRequestInspection } from "./btm-request-inspection";
import { BtmRequestDetailsReportedProducts } from "./btm-request-details-reported-products";
import { BtmRequestInspectionSummary } from "./btm-request-inspection-summary";
import { BtmShippingInfo } from "../model/btm-shipping-info";
import { t } from "i18next";
import { BtmReturnInspectionStatus } from "../model/btm-return-inspection-product";

import "./style.scss";

interface IBtmRequestDialogProps {
    readOnly?: boolean;
    request: BtmReturnRequest;
    onRequestUpdated?: (request: BtmReturnRequest) => void;
    onClose?: () => void;
}

export const BtmRequestDetails = (props: IBtmRequestDialogProps) => {
    return <div className={"btm-request-details__container"}>
        <Paper elevation={0} className={"btm-request-details__navigation"}>
            {props.onClose ?
                <Button onClick={props.onClose} sx={{ gap: "15px" }}>
                    <ArrowBack />
                    <Typography>{t("components.blueBrandReturns.page.details.back")}</Typography>
                </Button> : <div />}
            <BtmRequestStatusChip status={props.request.status} />
        </Paper>
        <div className={"btm-request-details__content"}>
            <div className={"btm-request-details__column"}>
                <Paper elevation={0} className={"btm-request-details__section btm-request-details__section--main"}>
                    <Typography variant={"h4"} color={"textPrimary"} className={"btm-request-details__title"}>
                        <Article color={"primary"} fontSize={"large"} />
                        <span>{t("components.blueBrandReturns.page.details.title.main", {
                            declarationId: props.request.declarationId,
                        })}</span>
                    </Typography>
                    <div className={"btm-request-details__table"}>
                        <div className={"btm-request-details__table-row"}>
                            <Typography color={"textSecondary"}>{t("components.blueBrandReturns.page.details.column.orderId")}</Typography>
                            <Typography fontSize={"large"}>{props.request.orderId}</Typography>
                        </div>
                        <div className={"btm-request-details__table-row"}>
                            <Typography color={"textSecondary"}>{t("components.blueBrandReturns.page.details.column.createdAt")}</Typography>
                            <Typography fontSize={"large"}>{props.request.insertDate.format("YYYY-MM-DD, HH:mm")}</Typography>
                        </div>
                    </div>
                </Paper>
                {props.request.status === "Processed" && props.onRequestUpdated &&
                    <BtmRequestInspection request={props.request} onCompleted={props.onRequestUpdated} />}
                {props.request.inspectionResult.filter(s => s.status !== BtmReturnInspectionStatus.None).length > 0 &&
                    <BtmRequestInspectionSummary request={props.request} />}
                <Paper elevation={0} className={"btm-request-details__section"}>
                    <Typography variant={"h4"} color={"textPrimary"}>
                        <LocalShipping color={"primary"} fontSize={"medium"} />
                        <span>{t("components.blueBrandReturns.page.details.title.trackingDetails")}</span>
                    </Typography>
                    <div className={"btm-request-details__table"}>
                        <div className={"btm-request-details__table-row"}>
                            <Typography color={"textSecondary"}>{t("components.blueBrandReturns.page.details.column.carrier")}</Typography>
                            <Typography fontSize={"large"}>
                                {props.request.carrier ?
                                    <Typography className={"btm-request-details__table-label"}>
                                        {props.request.carrier}
                                    </Typography>
                                    :
                                    <Typography component={"span"} className={"btm-request-details__table-label--empty"} color={"textSecondary"}>
                                        {t("components.blueBrandReturns.page.details.noCarrierSelected")}
                                    </Typography>}
                            </Typography>
                        </div>
                        <div className={"btm-request-details__table-row"}>
                            <Typography color={"textSecondary"}>
                                {props.request.shippingInfo.length <= 1 ?
                                    t("components.blueBrandReturns.page.details.column.trackingAndLabel") :
                                    t("components.blueBrandReturns.page.details.column.trackingAndLabelMultiple", {
                                        count: props.request.shippingInfo.length,
                                    })}
                            </Typography>
                            {props.request.isAppointmentRequested ?
                                <Typography fontSize={"large"}>
                                    {t("components.blueBrandReturns.page.details.carrierAppointmentRequested")}
                                </Typography> :
                                props.request.shippingInfo?.length > 0 ?
                                    <BtmRequestShippingDetails request={props.request} /> :
                                    <Typography component={"span"} className={"btm-request-details__table-label--empty"} color={"textSecondary"}>
                                        {t("components.blueBrandReturns.page.details.trackingDetailsNotProvided")}
                                    </Typography>}
                        </div>
                        <div className={"btm-request-details__table-row"}>
                            <Typography color={"textSecondary"}>
                                {t("components.blueBrandReturns.page.details.column.appointment")}
                            </Typography>
                            <Typography fontSize={"large"}>
                                {props.request.pickupDate ?
                                    `${props.request.pickupDate.format("YYYY-MM-DD")}`
                                        + `${props.request.serviceWindow ? ` (${t(`components.blueBrandReturns.serviceWindow.${props.request.serviceWindow}`)})` : ""}` :
                                    <Typography component={"span"} className={"btm-request-details__table-label--empty"} color={"textSecondary"}>
                                        {t("components.blueBrandReturns.page.details.pickupDateNotSpecified")}
                                    </Typography>}
                            </Typography>
                        </div>
                    </div>
                </Paper>
                <div className={"btm-request-details__content"}>
                    <Paper elevation={0} className={"btm-request-details__section"}>
                        <Typography variant={"h4"} color={"textPrimary"}>
                            <Person color={"primary"} fontSize={"medium"} />
                            <span>{t("components.blueBrandReturns.page.details.title.customerDetails")}</span>
                        </Typography>
                        <div className={"btm-request-details__table"}>
                            <div className={"btm-request-details__table-row"}>
                                <Typography color={"textSecondary"}>
                                    {t("components.blueBrandReturns.page.details.column.customerName")}
                                </Typography>
                                <Typography fontSize={"large"}>{props.request.customerName}</Typography>
                            </div>
                            <div className={"btm-request-details__table-row"}>
                                <Typography color={"textSecondary"}>
                                    {t("components.blueBrandReturns.page.details.column.contact")}
                                </Typography>
                                <Typography fontSize={"large"}>{props.request.customerPhoneNumber}</Typography>
                            </div>
                        </div>
                    </Paper>
                    <Paper elevation={0} className={"btm-request-details__section"}>
                        <Typography variant={"h4"} color={"textPrimary"}>
                            <Category color={"primary"} fontSize={"medium"} />
                            <span>{t("components.blueBrandReturns.page.details.title.productDetails")}</span>
                        </Typography>
                        <BtmRequestDetailsReportedProducts declaredProducts={props.request.declaredProducts} />
                    </Paper>
                </div>
            </div>
            <div className={"btm-request-details__column btm-request-details__column--right"}>
                <Paper elevation={0} className={"btm-request-details__section"}
                       sx={{ borderColor: props.onRequestUpdated && (props.request.status === "New" || props.request.status === "Pending") ?
                               VPUITheme.palette.primary.main : undefined }}>
                    <Typography variant={"h4"} color={"textPrimary"}>
                        {t("components.blueBrandReturns.page.details.title.logisticsSetup")}
                    </Typography>
                    <BtmRequestLogisticSetup readOnly={props.readOnly ?? false}
                                             request={props.request} onRequestUpdated={props.onRequestUpdated} />
                </Paper>
            </div>
        </div>
    </div>;
};

const BtmRequestShippingDetails = ({ request }: { request: BtmReturnRequest }) => {
    const handleLabelDownload = async (shippingInfo: BtmShippingInfo) => {
        if (!shippingInfo.file) {
            return;
        }

        FileSaver.saveAs(await FileExchangeToFile(shippingInfo.file));
    };

    return <div className={"btm-request-shipping"}>
        {request.shippingInfo?.length === 0 &&
            <Typography component={"span"} className={"btm-request-details__table-label--empty"} color={"textSecondary"}>
                {t("components.blueBrandReturns.page.details.trackingNotProvided")}
            </Typography>}
        {request.shippingInfo.map((shipping, index) =>
            <div className={"btm-request-shipping__item"} key={`tracking-${index}`}>
                <Typography className={"btm-request-shipping__item-text"}>{shipping.tracking}</Typography>
                {shipping.file && <Button onClick={() => handleLabelDownload(shipping)}
                        endIcon={<Download />} size={"small"} variant={"outlined"} color={"inherit"}>
                    {t("components.blueBrandReturns.page.details.downloadLabel")}
                </Button>}
            </div>)}
    </div>;
};
